import { SACI } from "@eldrun/content";
import { CylinderGeometry, Mesh, type Object3D, type ShaderMaterial } from "three";
import type { AtmosphereUniforms } from "../render/atmosphere.ts";
import { createWhirlwindMaterial } from "../render/shaders/whirlwind.ts";

export interface WhirlwindLike {
  x: number;
  z: number;
}

const HEIGHT = 7;
/** Segundos para sumir depois de capturado (Peneira) ou do anoitecer. */
const FADE_S = 1;

interface View {
  mesh: Mesh;
  material: ShaderMaterial;
  x: number;
  z: number;
  /** Sumindo (já saiu do estado): de 1 a 0. */
  fading: number | undefined;
}

/** Opacidade pela distância à câmera: inteiro até 45 m, some até 60 m (doc 06: visível a 60 m). */
export function whirlwindOpacity(distance: number): number {
  const fadeFrom = SACI.visible * 0.75;
  if (distance <= fadeFrom) return 1;
  return Math.max(0, 1 - (distance - fadeFrom) / (SACI.visible - fadeFrom));
}

/** Redemoinhos do Saci: funil com o shader próprio, posição suavizada e sumiço ao sair do estado. */
export class WhirlwindLayer {
  private readonly root: Object3D;
  private readonly atmosphere: AtmosphereUniforms;
  private readonly geometry = new CylinderGeometry(1.7, 0.25, HEIGHT, 20, 6, true).translate(0, HEIGHT / 2, 0);
  private readonly views = new Map<string, View>();
  /** Capturado: os que somem agora se acalmam (o funil some devagar). */
  calm = false;

  constructor(root: Object3D, atmosphere: AtmosphereUniforms) {
    this.root = root;
    this.atmosphere = atmosphere;
  }

  update(winds: Iterable<[string, WhirlwindLike]>, height: (x: number, z: number) => number, cameraX: number, cameraZ: number, dt: number): void {
    const seen = new Set<string>();
    for (const [id, w] of winds) {
      seen.add(id);
      let view = this.views.get(id);
      if (!view) {
        const material = createWhirlwindMaterial(this.atmosphere, HEIGHT);
        const mesh = new Mesh(this.geometry, material);
        mesh.renderOrder = 6;
        this.root.add(mesh);
        view = { mesh, material, x: w.x, z: w.z, fading: undefined };
        this.views.set(id, view);
        this.calm = false;
      }
      // Suaviza entre os patches (20 Hz).
      const k = Math.min(1, dt * 10);
      view.x += (w.x - view.x) * k;
      view.z += (w.z - view.z) * k;
    }
    for (const [id, view] of this.views) {
      if (!seen.has(id) && view.fading === undefined) view.fading = 1;
      if (view.fading !== undefined) {
        view.fading -= dt / FADE_S;
        view.material.uniforms["uCalmo"]!.value = this.calm ? 1 - Math.max(0, view.fading) : 0;
        if (view.fading <= 0) {
          this.root.remove(view.mesh);
          view.material.dispose();
          this.views.delete(id);
          continue;
        }
      }
      view.mesh.position.set(view.x, height(view.x, view.z), view.z);
      const fade = view.fading ?? 1;
      view.material.uniforms["uOpacidade"]!.value = whirlwindOpacity(Math.hypot(view.x - cameraX, view.z - cameraZ)) * fade;
    }
  }

  /** Redemoinho mais perto de (x, z) a até `reach` m (Peneira), com a distância. */
  nearest(x: number, z: number, reach: number): { id: string; d: number } | undefined {
    let best: { id: string; d: number } | undefined;
    for (const [id, v] of this.views) {
      if (v.fading !== undefined) continue;
      const d = Math.hypot(v.x - x, v.z - z);
      if (d <= reach && (!best || d < best.d)) best = { id, d };
    }
    return best;
  }

  get count(): number {
    return [...this.views.values()].filter((v) => v.fading === undefined).length;
  }

  dispose(): void {
    for (const view of this.views.values()) {
      this.root.remove(view.mesh);
      view.material.dispose();
    }
    this.views.clear();
    this.geometry.dispose();
  }
}
